import Breadcrumb, { Crumb } from "../../../components/common/Breadcrumbs";
import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Form from "../../../components/common/form/Form";
import { useCreateCountyMutation, useGetCountyByIdQuery, useUpdateCountyMutation } from "./countiesApi";
import { skipToken } from "@reduxjs/toolkit/query";
import InputField from "../../../components/common/form/InputField";
import { OptionType } from "../../../helpers/utils/StyleUtils";
import {
    addCountyCertification,
    createOptionListForSelectTag,
    deleteCountyCertification,
    validateCountyCertificate
} from "../../../helpers/utils/Utils";
import SelectField from "../../../components/common/form/SelectField";
import { useGetAllStatesQuery } from "../states/statesApi";
import { handleRTKQuery } from "../../../helpers/utils/RtkQueryUtils";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import { Routes as AppRoutes } from "../../../routes";
import ButtonTray from "../../../components/common/form/ButtonTray";
import CommonChosenInput from "../../order/editor/Sections/CommonChosenInput";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";

interface props {
    crumbs: Crumb[];
    title: string,
    btnText: string,
    isReadOnly?: boolean
}

const CountyForm: FC<props> = ({ crumbs, title, btnText, isReadOnly = false }) => {

    const navigate = useNavigate();
    let { countyId } = useParams();

    const [createCounty, createCountyApiResponse] = useCreateCountyMutation();
    const [updateCounty, updateCountyApiResponse] = useUpdateCountyMutation();
    const countyByIdApi = useGetCountyByIdQuery(countyId ?? skipToken);
    const [countyData, setCountyData] = useState<any>({});
    const statesApiResult = useGetAllStatesQuery("");
    const [id, setId] = useState('');
    const [certifications, setCertifications] = useState([]);
    const [certificationEditYear, setCertificationEditYear] = useState("");
    const [certificationEditError, setCertificationEditError] = useState<any>({});
    const [aliases, setAliases] = useState([]);

    useEffect(() => {
        let obj = countyByIdApi?.data;
        if (obj) {
            setCountyData({
                ...obj,
                state: {
                    label: obj.state?.stateName,
                    value: obj.state?.id,
                }
            })
            setAliases(obj?.aliases.map((alias: any) => ({
                label: alias,
                value: alias
            })));
            setCertifications(obj?.certifications)
        }

    }, [countyByIdApi?.data]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        let commonObj = {
            ...countyData,
            state: statesApiResult?.data.filter((el: any) => el.id === countyData.state.value)?.[0],
            certifications: certifications,
            aliases: aliases.map((alias: any) => alias?.value),
            stateAbbreviation: statesApiResult?.data.filter((el: any) => el.id === countyData.state.value)?.[0].stateAbbreviation,
        };

        const createOrUpdateCounty = async () => {
            if (btnText !== "Update") {
                return await createCounty(commonObj).unwrap();
            } else {
                return await updateCounty({
                    id: countyId,
                    requestBody: commonObj,
                }).unwrap();
            }
        };

        await handleRTKQuery(createOrUpdateCounty, (response: any) => {
            if (response?.success || response?.data?.success) {
                setCountyData({});
                navigate("/counties");
            }
        });
    }

    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;
        setCountyData({
            ...countyData,
            [name]: value,
        })
    }

    return (
        <>
            <div className="px-4 py-2">

                <div className={"justify-between sm:block md:flex lg:flex"}>
                    <Breadcrumb crumbs={crumbs} />
                    {hasPermission(["AUDIT-V"]) &&
                        <AuditableInfoButton obj={countyByIdApi?.data} />}
                </div>

                <div className="flex justify-center mt-3">
                    <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                        <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-5">{title}</h3>
                        <Form onSubmit={handleSubmit} fields={<>
                            <div className="grid grid-cols-12">
                                <InputField
                                    name={"countyName"}
                                    label="County Name"
                                    onChange={handleOnChange}
                                    value={countyData?.countyName || ""}
                                    type="text"
                                    placeholder="Enter Name..."
                                    required={true}
                                    disabled={isReadOnly}
                                />
                                <div className="col-span-12 grid grid-cols-12 gap-3 mt-2">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 flex">
                                        <label className="min-w-[100px]">Aliases</label>
                                    </div>

                                    <div className="relative col-span-9 flex items-center gap-3">
                                        <div className="w-full">
                                            <CommonChosenInput
                                                placeholder={"Enter Aliases..."}
                                                state={aliases}
                                                setState={setAliases}
                                                isRequired={false} isReadOnly={isReadOnly} />
                                        </div>
                                    </div>
                                </div>


                                <InputField
                                    name={"fullFipsCode"}
                                    label="FIPS Code"
                                    onChange={handleOnChange}
                                    value={countyData?.fullFipsCode || ""}
                                    type="number"
                                    placeholder="Enter FIPS Code..."
                                    required={true}
                                    disabled={isReadOnly}
                                />
                                <SelectField
                                    placeholder='Select State...'
                                    value={countyData?.state}
                                    options={createOptionListForSelectTag(statesApiResult?.data, "stateName", "id")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "state", value: selectedOption })
                                    }}
                                    label={"State"}
                                    required={true}
                                    isDisabled={isReadOnly}
                                />


                                <div className="col-span-12 grid grid-cols-12 gap-3">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                        <label className="min-w-[100px]">Certified Years</label>
                                    </div>

                                    <div className="relative col-span-9 flex items-center gap-3">
                                        <div className="w-full">
                                            <InputField
                                                className={'form-control' + (certificationEditError?.year ? ' error_style' : '')}
                                                type="number"
                                                minLength={4} step={1} maxLength={4} name="year"
                                                value={certificationEditYear}
                                                onChange={(e) => {
                                                    setCertificationEditYear(e.target.value)
                                                }}
                                                hideLabel
                                                placeholder="Enter Year..."
                                                disabled={isReadOnly} />
                                            <p className="mb-0">{certificationEditError.year}</p>
                                        </div>

                                        {isReadOnly === false && <div className="col-sm-2 mt-2">
                                            <i className="fa fa-plus-circle m-md-0 cursor"
                                                title="Add Certification..." data-backdrop="static"
                                                onClick={() => {
                                                    let validationResult = addCountyCertification(id,
                                                        certifications,
                                                        setCertifications,
                                                        certificationEditYear);

                                                    setCertificationEditError(validationResult);

                                                    if (validationResult.success) {
                                                        setCertificationEditYear("");
                                                    }
                                                }} />
                                        </div>}
                                    </div>
                                </div>


                                {certifications?.map((certification: any, index: any) => {
                                    return (<>
                                        <div className="col-span-12 grid grid-cols-12 gap-3">
                                            <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                            </div>
                                            <div className="relative col-span-9 flex items-center gap-3">
                                                <div className="w-full">
                                                    <InputField
                                                        type="number"
                                                        name="year"
                                                        value={certification.taxYear}
                                                        placeholder="Enter Year..."
                                                        hideLabel
                                                        disabled={true} />
                                                </div>

                                                {isReadOnly === false && <div className="col-sm-2 mt-1">
                                                    <i className="fa fa-trash m-md-0 cursor"
                                                        title="Delete Certified Year..."
                                                        data-backdrop="static"
                                                        onClick={() => {
                                                            deleteCountyCertification(index, setCertifications);
                                                        }}
                                                    />
                                                </div>}
                                            </div>
                                        </div>

                                    </>)
                                })}
                            </div>
                            {!isReadOnly &&
                                <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    </div>
                                    <div className="relative col-span-9">
                                        <div className={"mt-4 flex"}>
                                            <ButtonTray
                                                buttons={[
                                                    {
                                                        buttonProps: {
                                                            btnText: "Cancel",
                                                            onClick: () => navigate(AppRoutes.Counties.path),
                                                            type: "cancel",
                                                        },
                                                        buttonType: "button"
                                                    },
                                                    {
                                                        buttonProps: {
                                                            btnText: "Submit",
                                                            type: "submit",
                                                            isLoading: (createCountyApiResponse.isLoading || updateCountyApiResponse.isLoading),

                                                        },
                                                        buttonType: "button",

                                                    }
                                                ]}
                                                align="end"
                                                gap={2}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }


                        </>}
                        />

                    </div>
                </div>
            </div>
        </>
    )
}
export default CountyForm;